import {
  MarkerClusterer,
  SuperClusterAlgorithm,
} from '@googlemaps/markerclusterer'

/**
 * Mixin that handles marker clustering functionality.
 *
 * Responsibilities:
 * - Initializing the marker clusterer
 * - Creating custom cluster markers with SVG styling
 * - Managing cluster visibility and positioning
 * - Handling cluster marker interactions
 *
 * The mixin expects the following to be available in the component:
 * - this.map: Google Maps instance
 * - this.markers: Array of markers to cluster
 *
 * Uses @googlemaps/markerclusterer for clustering functionality
 */
export default {
  data: () => ({
    markerClusterAlgorithm: null,
    markerClusterRenderer: null,
    markerCluster: null,
  }),

  methods: {
    initializeClusterer() {
      this.markerClusterAlgorithm = new SuperClusterAlgorithm({
        radius: 240,
        maxZoom: 2,
        minPoints: 4,
      })

      this.markerClusterRenderer = {
        render: ({ count, position }) => {
          // Create SVG element for the cluster marker
          const markerSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
          markerSvg.setAttribute('viewBox', '0 0 250 250')
          markerSvg.setAttribute('width', '75')
          markerSvg.setAttribute('height', '75')

          // Add the cluster circles
          const g = document.createElementNS('http://www.w3.org/2000/svg', 'g')
          g.setAttribute('transform', 'matrix(1, 0, 0, 1, -37.664764, -137.482788)')

          const circle1 = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
          circle1.setAttribute('cx', '120')
          circle1.setAttribute('cy', '120')
          circle1.setAttribute('opacity', '.8')
          circle1.setAttribute('r', '80')
          circle1.setAttribute('style', 'stroke-dasharray: 55; stroke-dashoffset: 28px; fill: rgba(206, 183, 128, 0.8); paint-order: fill; stroke-linecap: square; stroke-miterlimit: 10; fill-rule: nonzero; stroke-width: 10px;')
          circle1.setAttribute('transform', 'matrix(1, 0, 0.015846, 1, 40.468361, 144.150238)')

          const circle2 = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
          circle2.setAttribute('cx', '120')
          circle2.setAttribute('cy', '120')
          circle2.setAttribute('opacity', '.8')
          circle2.setAttribute('r', '74.5')
          circle2.setAttribute('style', 'paint-order: fill; fill-rule: nonzero; stroke: rgb(142, 105, 25); stroke-width: 10px; stroke-linecap: square; stroke-miterlimit: 6; stroke-dashoffset: 25px; fill: rgba(255, 255, 255, 0); stroke-dasharray: 60, 55;')
          circle2.setAttribute('transform', 'matrix(1, 0, 0.015846, 1, 40.443767, 143.982761)')

          g.appendChild(circle1)
          g.appendChild(circle2)
          markerSvg.appendChild(g)

          // Create text element for count
          const textContainer = document.createElementNS('http://www.w3.org/2000/svg', 'g')
          textContainer.setAttribute('transform', 'matrix(1, 0, 0, 1, -37.664764, -137.482788)')

          const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
          text.setAttribute('x', '160')
          text.setAttribute('y', '264')
          text.setAttribute('text-anchor', 'middle')
          text.setAttribute('dominant-baseline', 'central')
          text.setAttribute('fill', '#000000')
          text.setAttribute('font-size', '48px')
          text.setAttribute('font-weight', '700')
          text.textContent = count

          textContainer.appendChild(text)
          markerSvg.appendChild(textContainer)

          // Create cluster position using LatLng
          const clusterPosition = new google.maps.LatLng(
            parseFloat(position.lat()),
            parseFloat(position.lng())
          )

          const clusterMarker = new google.maps.marker.AdvancedMarkerElement({
            position: clusterPosition,
            content: markerSvg,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          })

          // Add compatibility methods for cluster marker
          clusterMarker.getPosition = () => clusterPosition
          clusterMarker.getVisible = () => !clusterMarker.hidden
          clusterMarker.setVisible = (visible) => {
            if (visible) {
              clusterMarker.map = clusterMarker.oldMap
              delete clusterMarker.oldMap
              clusterMarker.hidden = false
            } else {
              clusterMarker.oldMap = clusterMarker.map
              clusterMarker.map = null
              clusterMarker.hidden = true
            }
          }

          return clusterMarker
        },
      }

      this.markerCluster = new MarkerClusterer({
        map: this.map,
        markers: this.markers,
        algorithm: this.markerClusterAlgorithm,
        renderer: this.markerClusterRenderer,
      })
    }
  }
}
